/* eslint-disable @typescript-eslint/no-explicit-any -- casts formulaire / erreurs axios */
/* eslint-disable react-hooks/exhaustive-deps -- effets alignés chargement id (pattern U2101) */
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import U2000DefaultForm from '../../u2000Core/components/formControls/U2000DefaultForm';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useU2000SearchHandlers } from '../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import { useU2000GetHandlers } from '../../u2000Core/hooks/handler/U2000UseGetHandlers';
import { useU2000SaveHandlers } from '../../u2000Core/hooks/handler/U2000UseSaveHandlers';
import type { U2000SearchPayload, U2000SearchResult } from '../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from '../../u2000Core/reducer/formReducer/U2000FormState';
import { U2000LUStateEnumNextAction } from '../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from '../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { NS, U3202ROLES } from '../U3202';
import U3202OrgProviderConfigBody from './form/U3202OrgProviderConfigBody';
import { U3202OrgProviderConfigFormToolBar } from './form/U3202OrgProviderConfigFormToolBar';
import U3202OrgProviderConfigDeleteDlg from './common/U3202OrgProviderConfigDeleteDlg';
import { useU3202Context } from '../U3202Context';
import U2000AuditPanelComponent from '../../u2000Core/components/audit/U2000AuditPanelComponent';
import { useU2000AuthContext } from '../../u2000Core/context/auth/U2000UseAuthContext';
import {
  createEmptyU3202OrgProviderConfigFormData,
  doDeleteProvider,
  doGetProviderAudits,
  doSearchProviders,
  doUpdateProvider,
  luHandleAppiError,
  normalizeU3202OrgProviderConfigFromApi,
  u3202FormDataToSearchRow,
  type U3202OrgProviderConfigFormData,
  type U3202OrgProviderConfigSearchRow,
} from '../U3202Service';
import {
  u3202BuildProviderUpdateBody,
  u3202ValidateOrgProviderConfigForm,
} from '../utils/u3202ParameterFormUtils';
import { u3202LoadProviderDetail } from '../utils/u3202OrgProviderConfigLoadUtils';
import { u3202CreateProviderWithModels } from '../utils/u3202PersistModelChanges';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import U2000Tab from '../../u2000Core/components/tab/U2000Tab';
import U3202OrgModelConfigBody from './form/u3202OrgModelConfigTab/U3202OrgModelConfigBody';

export interface U3202OrgProviderConfigFormExtendedHandlers extends U2000FormStateHandlersReturn<U3202OrgProviderConfigFormData> {
  addNewConfig: () => void;
  modifyConfig: () => void;
  deleteConfig: () => void;
}

const U3202OrgProviderConfigForm: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3202Context();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const listTitle = t('configList.title', { ns: NS });
  const isCreate = id === 'new';

  // Traduit les codes d'erreur de validation (bornes de paramètres, etc.) en messages i18n.
  const translateValidationErrors = useCallback(
    (errors: Record<string, string>, row: U3202OrgProviderConfigFormData) => {
      const translated: Record<string, string> = {};
      for (const [key, code] of Object.entries(errors)) {
        if (code === 'errParameterOutOfRange') {
          const match = key.match(/models\.(\d+)\.parameterValues\.(\w+)/);
          const modelIndex = match ? Number(match[1]) : 0;
          const param = match?.[2] ?? '';
          const modelRow = row.models[modelIndex];
          const field = modelRow?.parameterSchema?.[param];
          translated[key] = t('errParameterOutOfRange', {
            ns: NS,
            param,
            min: field?.min ?? '',
            max: field?.max ?? '',
          });
        } else if (code === 'errInvalidParameter') {
          const param = key.split('.').pop() ?? '';
          translated[key] = t('errInvalidParameter', { ns: NS, param });
        } else {
          translated[key] = t(code, { ns: NS });
        }
      }
      return translated;
    },
    [t],
  );

  const validateForm = useCallback(
    (row: U3202OrgProviderConfigFormData) =>
      translateValidationErrors(u3202ValidateOrgProviderConfigForm(row, !row.id), row),
    [translateValidationErrors],
  );

  const updateBreadcrumb = useCallback(
    (data: U3202OrgProviderConfigFormData | null, creating: boolean) => {
      if (creating) {
        setLogicalUnitBreadcrumb([
          { href: '/U3202', label: listTitle },
          { label: t('create.title', { ns: NS }) },
        ]);
        return;
      }
      if (data?.providerName) {
        setLogicalUnitBreadcrumb([
          { href: '/U3202', label: listTitle },
          { label: `${data.providerName} (${data.providerCode})` },
        ]);
      }
    },
    [listTitle, setLogicalUnitBreadcrumb, t],
  );

  const u3202BL: U2000FormSBusinessHandlers<U3202OrgProviderConfigFormData> = {
    newRecord: () => createEmptyU3202OrgProviderConfigFormData(),

    validateForm,

    // Validation puis délégation au handler de sauvegarde (pattern U2101).
    saveForme: async (row, editMode) => {
      const errors = validateForm(row);
      if (Object.keys(errors).length === 0) {
        await handleSave.saveData(row, editMode);
      } else {
        u3202FormExtended.setErrors(errors);
      }
    },

    cancelForm: () => {
      const old = u3202FormExtended.getOldData();
      if (old) {
        u3202FormExtended.setData({ ...old });
      }
      if (luStateHandlers.getActionOriginFrom() === '/U3202') {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      } else if (isCreate) {
        navigate('/U3202');
      }
    },

    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },

    onChangePageForm: (page, direction) => {
      handleSearch.handleSearch(
        searchStateHandlers.extractSearchParameter(),
        gridStateHandlers.extractPagination(page),
        direction,
      );
    },

    onLeaveForm: () => {
      navigate('/U3202');
    },
  };

  const u3202FormHandlers = useU2000FormStateHandlers<U3202OrgProviderConfigFormData>({
    formState,
    formStateDispatch,
    businessHandlers: u3202BL,
  });

  const u3202FormExtended: U3202OrgProviderConfigFormExtendedHandlers = {
    ...u3202FormHandlers,
    addNewConfig: () => {
      navigate('/U3202/new');
    },
    modifyConfig: () => {
      u3202FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteConfig: () => {
      handleDelete.handleDelete();
    },
  };

  // Recherche liste (pattern U2101).
  const handleSearch = useU2000SearchHandlers<U3202OrgProviderConfigSearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearchProviders(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3202OrgProviderConfigSearchRow>, direction) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      const row = searchResult.data[idx];
      if (row?.id) {
        handleGet.getData(row.id);
      }
    },
    onError: ({ error }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Chargement fiche (pattern U2101) : `doGetFn` orchestre le double GET
  // (config + catalogue fournisseur) et renvoie la donnée formulaire normalisée.
  const handleGet = useU2000GetHandlers<U3202OrgProviderConfigFormData & { id: string }>({
    doGetFn: async (providerId: string) => {
      const provider = await u3202LoadProviderDetail(u2000AxiosAuthenticated, providerId);
      return { data: { result: normalizeU3202OrgProviderConfigFromApi(provider) } };
    },
    onSuccess: (normalized) => {
      gridStateHandlers.setDataById(normalized.id, u3202FormDataToSearchRow(normalized));
      u3202FormExtended.setData(normalized);
      updateBreadcrumb(normalized, false);

      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(normalized.id));
      navigate(newPath, { replace: true });

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3202FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: ({ error }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      navigate('/U3202');
    },
  });

  // Sauvegarde (pattern U2101) : `doSaveFn` orchestre catalogue + create/update et
  // renvoie la config (create) ou l'id (update) ; `onSuccess` recharge la fiche.
  const handleSave = useU2000SaveHandlers<U3202OrgProviderConfigFormData>({
    doSaveFn: async (data, editMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        const provider = await u3202CreateProviderWithModels({ axios: u2000AxiosAuthenticated, data });
        return { data: { result: provider } };
      }
      await doUpdateProvider(u2000AxiosAuthenticated, data.id, u3202BuildProviderUpdateBody(data));
      return { data: { result: { id: data.id } } };
    },
    onSuccess: async (result, editMode) => {
      const configId = result.id;
      const provider = await u3202LoadProviderDetail(u2000AxiosAuthenticated, configId);
      const normalized = normalizeU3202OrgProviderConfigFromApi(provider);
      const searchRow = u3202FormDataToSearchRow(normalized);

      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), searchRow]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length - 1);
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(configId));
        navigate(newPath, { replace: true });
      } else {
        gridStateHandlers.setDataById(configId, searchRow);
      }

      u3202FormExtended.afterSaveData(normalized);
      u3202FormExtended.setData(normalized);
      toast.success(
        t(editMode === U2000FormStateEnumEditMode.ADD ? 'create.success' : 'save.success', { ns: NS }),
        t('successTitle', { ns: NS }),
      );

      if (
        editMode === U2000FormStateEnumEditMode.EDIT &&
        (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD ||
          luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) &&
        luStateHandlers.getActionOriginFrom() === '/U3202'
      ) {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },
    onError: ({ error }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3202FormExtended.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3202FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => {
      const data = u3202FormExtended.getData();
      return data?.id ? [u3202FormDataToSearchRow(data)] : [];
    },
    doDeleteFn: async (rowId, row) => {
      return await doDeleteProvider(u2000AxiosAuthenticated, rowId, {
        id: row.id,
        version: row.version,
      });
    },
    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(
          t('delete.success', { provider: row.name ?? row.id, ns: NS }),
          t('successTitle', { ns: NS }),
        );
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(
        currentIdx,
        newData,
        totalRows,
      );
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.PREVIOUSPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate('/U3202', { replace: true });
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // Initialisation au montage et quand id change (pattern U2101).
  useEffect(() => {
    if (id === undefined) return;

    if (id === 'new') {
      u3202FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3202FormExtended.setData(createEmptyU3202OrgProviderConfigFormData());
      updateBreadcrumb(null, true);
      return;
    }

    handleGet.getData(id);
  }, [id]);

  const isCreateMode = u3202FormExtended.getFormEditMode() === U2000FormStateEnumEditMode.ADD;
  const currentData = u3202FormExtended.getData();
  const providerId = currentData?.id;
  // Onglet « Modèles » (grille détail maître/détail, multi-modèles) disponible en
  // consultation/édition et en création — y compris pour un nouveau fournisseur.
  const showModelTab = !!currentData && (currentData.id != null || isCreateMode);
  const formTabs = [
    showModelTab && {
      id: 'tabOrgModelConfig',
      label: t('tabOrgModelConfig', { ns: NS }),
      content: (
        <U3202OrgModelConfigBody
          parentFormStateHandlers={u3202FormExtended as U3202OrgProviderConfigFormExtendedHandlers}
        />
      ),
    },
    hasRole(U3202ROLES.AUDITS) &&
      !isCreateMode &&
      providerId && {
        id: 'tabAudits',
        label: t('tabAudits', { ns: NS }),
        content: (
          <U2000AuditPanelComponent
            id={providerId}
            doGetAuditsData={doGetProviderAudits}
            toastName="U3202ProviderAudit"
          />
        ),
      },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  return (
    <>
      <U3202OrgProviderConfigDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000DefaultForm
        actionsBtns={<U3202OrgProviderConfigFormToolBar formStateHandlers={u3202FormExtended as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3202FormExtended as any}
      >
        <U3202OrgProviderConfigBody
          formStateHandlers={u3202FormExtended as any}
          t={t}
        />
        {formTabs.length > 0 && (
          <U2000Tab
            className="mt-4"
            tabs={formTabs}
            defaultTab="tabOrgModelConfig"
            activeTab={activeTab ?? 'tabOrgModelConfig'}
            setActiveTab={(tab) => setActiveTab(typeof tab === 'string' ? tab : String(tab))}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3202OrgProviderConfigForm;
